type RiskMatrixData = {
  industry: string;
  entity_size_score: number;
  risk_assessment: {
    attack_category: string;
    nature_of_attack: number;
    impact: string;
    threat_actors: Record<string, { probability: string; score: number }>;
  }[];
};

const ACTOR_PROBABILITY_COLUMNS: Record<string, string> = {
  competitori: "E",
  activisti: "G",
  teroristi: "I",
  infractori: "K",
  actori: "M",
};

const IMPACT_COLUMN = "D";
const DATA_START_ROW = 9;

const IMPACT_LEVEL_ORDER: Record<string, number> = {
  Scăzut: 1,
  Mediu: 2,
  Ridicat: 3,
};

const PROBABILITY_LEVEL_ORDER: Record<string, number> = {
  Scăzută: 1,
  Medie: 2,
  Ridicată: 3,
};

export type ModificationRow = [string, string, string];

export function collectMatrixModifications(
  matrix: RiskMatrixData,
  defaults: RiskMatrixData,
  sectorId: string,
  sectorName: string
): ModificationRow[] {
  const modifications: ModificationRow[] = [];
  const sheetName = `${sectorId}_${sectorName}`;

  for (let i = 0; i < matrix.risk_assessment.length; i++) {
    const current = matrix.risk_assessment[i];
    const defaultEntry = defaults.risk_assessment[i];
    if (!defaultEntry) continue;

    const row = DATA_START_ROW + i;

    if (current.impact !== defaultEntry.impact) {
      const currentLevel = IMPACT_LEVEL_ORDER[current.impact] ?? 0;
      const defaultLevel = IMPACT_LEVEL_ORDER[defaultEntry.impact] ?? 0;
      const direction = currentLevel < defaultLevel ? "scăzut" : "crescut";
      const cellRef = `${IMPACT_COLUMN}${row}`;
      modifications.push([
        `Foaia de lucru: ${sheetName} / Celula: «${cellRef}». [I] a fost ${direction}.`,
        "Da",
        `${sectorId}I${cellRef}`,
      ]);
    }

    for (const actorKey of Object.keys(current.threat_actors)) {
      const currentActor = current.threat_actors[actorKey];
      const defaultActor = defaultEntry.threat_actors[actorKey];
      if (!defaultActor) continue;

      if (currentActor.probability !== defaultActor.probability) {
        const currentLevel =
          PROBABILITY_LEVEL_ORDER[currentActor.probability] ?? 0;
        const defaultLevel =
          PROBABILITY_LEVEL_ORDER[defaultActor.probability] ?? 0;
        const direction = currentLevel < defaultLevel ? "scăzut" : "crescut";
        const col = ACTOR_PROBABILITY_COLUMNS[actorKey];
        if (!col) continue;
        const cellRef = `${col}${row}`;
        modifications.push([
          `Foaia de lucru: ${sheetName} / Celula: «${cellRef}». [P] a fost ${direction}.`,
          "Da",
          `${sectorId}P${cellRef}`,
        ]);
      }
    }
  }

  return modifications;
}

export function filterValidJustifications(
  justifications: Record<string, string>,
  modifications: ModificationRow[]
): Record<string, string> {
  const validModIds = new Set(modifications.map((mod) => mod[2]));
  return Object.fromEntries(
    Object.entries(justifications).filter(([modId]) => validModIds.has(modId))
  );
}

export function computeSectorIdentifier(
  sectorId: string,
  modificationsCount: number,
  score: number
): string {
  const flag = modificationsCount > 0 ? 1 : 0;
  const scorePart = parseInt(Math.floor(score).toString(), 16);
  return `${flag}${sectorId}${modificationsCount}${scorePart}`;
}
